/* Progressive enhancement: keep all panels and ordinary product links in Liquid. */
(() => {
  'use strict';
  const initialize = root => {
    if (root.dataset.mfInitialized) return;
    root.dataset.mfInitialized = 'true';
    const query = (selector, scope = root) => [...scope.querySelectorAll(selector)];
    const needPanels = query('[data-need-panel]');
    const validNeeds = new Set(needPanels.map(panel => panel.dataset.needPanel));
    const selectedDemo = new Map();
    const storageKey = 'morfone-home-selection-v1';
    const readState = () => { try { return JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch (_) { return {}; } };
    const saveState = value => { try { sessionStorage.setItem(storageKey, JSON.stringify({ ...readState(), ...value })); } catch (_) { /* Storage can be unavailable. */ } };
    const paused = scope => query('video,audio', scope).forEach(media => media.pause());
    const defaultNeed = validNeeds.has('back') ? 'back' : [...validNeeds][0];
    let activeNeed = defaultNeed;
    const hashNeed = () => { const match = location.hash.match(/^#care-(face|back|body|scalp)$/); return match && validNeeds.has(match[1]) ? match[1] : null; };
    const demoFor = (scope, handle) => {
      const panels = query('[data-demo-panel]', scope);
      if (!panels.length) return;
      if (!panels.some(panel => panel.dataset.demoPanel === handle)) handle = panels[0].dataset.demoPanel;
      panels.forEach(panel => { const hide = panel.dataset.demoPanel !== handle; if (hide) paused(panel); panel.hidden = hide; });
      query('[data-demo]', scope).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.demo === handle)));
      selectedDemo.set(scope, handle);
    };
    const chooseNeed = (need, options = {}) => {
      if (!validNeeds.has(need)) return;
      activeNeed = need;
      needPanels.forEach(panel => {
        const hide = panel.dataset.needPanel !== need;
        if (hide) paused(panel);
        panel.hidden = hide;
        if (!hide) {
          demoFor(panel, selectedDemo.get(panel));
          if (options.resetScroll) query('.mf-table-scroll', panel).forEach(table => { table.scrollLeft = 0; });
        }
      });
      query('[data-need]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.need === need)));
      root.dataset.activeNeed = need;
      saveState({ need });
      if (options.history && hashNeed() !== need) history.pushState({ ...history.state, mfNeed: need }, '', '#care-' + need);
      root.dispatchEvent(new CustomEvent('mf:need-change', { bubbles: true, detail: { need } }));
    };
    const scrollToSelection = () => root.querySelector('[data-mf-selection]')?.scrollIntoView({ block: 'start', behavior: 'auto' });
    query('[data-need]').forEach(button => button.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); chooseNeed(button.dataset.need, { history: true, resetScroll: true });
      if (!button.closest('[data-mf-selection]')) scrollToSelection();
    }));
    // Header anchors sit outside the section; reveal the hidden target before scrolling.
    document.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || root.contains(link) || link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      const match = url.hash.match(/^#care-(face|back|body|scalp)$/);
      if (!match || !validNeeds.has(match[1])) return;
      event.preventDefault(); chooseNeed(match[1], { history: true, resetScroll: true });
      link.closest('details[open]')?.removeAttribute('open');
      scrollToSelection();
    });
    query('[data-demo]').forEach(button => button.addEventListener('click', event => {
      event.preventDefault();
      const scope = button.closest('[data-need-panel]') || button.closest('[data-demo-group]') || root;
      demoFor(scope, button.dataset.demo);
    }));
    ['data-compare', 'data-more-specs'].forEach(attribute => {
      const details = query('details[' + attribute + ']');
      const stored = readState()[attribute];
      if (typeof stored === 'boolean') details.forEach(item => { item.open = stored; });
      details.forEach(item => item.addEventListener('toggle', () => {
        // Ignore queued toggles from a hidden previous group; its state is mirrored below.
        const panel = item.closest('[data-need-panel]');
        if (panel && panel.hidden) return;
        details.forEach(other => { if (other !== item && other.open !== item.open) other.open = item.open; });
        saveState({ [attribute]: item.open });
      }));
    });
    const faqPanels = query('[data-faq-panel]');
    const showFaq = key => {
      if (!faqPanels.some(panel => panel.dataset.faqPanel === key)) return;
      faqPanels.forEach(panel => { panel.hidden = panel.dataset.faqPanel !== key; });
      query('[data-faq]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.faq === key)));
    };
    query('[data-faq]').forEach(button => button.addEventListener('click', () => showFaq(button.dataset.faq)));
    if (faqPanels.length) showFaq(faqPanels[0].dataset.faqPanel);
    let currentDialog = null, opener = null, isPinned = false, hoverTimer, leaveTimer;
    const closePreview = (restore = true) => {
      clearTimeout(hoverTimer); clearTimeout(leaveTimer);
      if (!currentDialog) return;
      const dialog = currentDialog, trigger = opener, pinned = isPinned;
      currentDialog = null; opener = null; isPinned = false;
      paused(dialog);
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      dialog.classList.remove('mf-dialog--hover');
      root.dataset.previewOpen = 'false';
      root.dispatchEvent(new CustomEvent('mf:preview-close', { bubbles: true }));
      if (restore && pinned && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
    const openPreview = (trigger, pin) => {
      clearTimeout(hoverTimer); clearTimeout(leaveTimer);
      const dialog = document.getElementById(trigger.dataset.previewTarget);
      if (!dialog || dialog.tagName !== 'DIALOG') return;
      if (!pin && (document.querySelector('dialog:modal') || document.activeElement?.matches('input,textarea,select'))) return;
      if (currentDialog === dialog && (!pin || isPinned)) return;
      closePreview(false); currentDialog = dialog; opener = trigger; isPinned = pin;
      // Hover is non-modal and does not steal focus. A deliberate click pins a modal.
      if (pin && typeof dialog.showModal === 'function') dialog.showModal();
      else { dialog.setAttribute('open', ''); dialog.classList.toggle('mf-dialog--hover', !pin); }
      root.dataset.previewOpen = 'true';
      root.dispatchEvent(new CustomEvent('mf:preview-open', { bubbles: true, detail: { pinned: pin } }));
    };
    const scheduleClose = () => { clearTimeout(leaveTimer); if (!isPinned) leaveTimer = setTimeout(() => closePreview(false), 220); };
    query('[data-preview-target]').forEach(trigger => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault(); openPreview(trigger, true);
        // A deliberate Watch click starts playback; hover alone stays silent.
        const media = currentDialog?.querySelector('video');
        if (media) media.play().catch(() => { /* Native controls remain available if playback is blocked. */ });
      });
      // Large previews require an explicit click; moving across products never opens a dialog.
      const dialog = document.getElementById(trigger.dataset.previewTarget);
      if (!dialog || dialog.dataset.mfBound) return;
      dialog.dataset.mfBound = 'true';
      dialog.addEventListener('pointerenter', () => clearTimeout(leaveTimer));
      dialog.addEventListener('pointerleave', scheduleClose);
      dialog.addEventListener('cancel', event => { event.preventDefault(); closePreview(); });
      dialog.addEventListener('close', () => {
        // A queued close from hover-to-modal promotion must not close the reopened dialog.
        if (dialog.open) return;
        if (currentDialog === dialog) closePreview(); else paused(dialog);
      });
      dialog.addEventListener('click', event => {
        if (event.target.closest('[data-preview-close]')) { closePreview(); return; }
        if (event.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closePreview();
        }
      });
      // Starting media pins a hover preview so it cannot vanish mid-session.
      query('video,audio', dialog).forEach(media => media.addEventListener('play', () => {
        if (currentDialog === dialog && !isPinned) { isPinned = true; clearTimeout(leaveTimer); clearTimeout(hoverTimer); /* Keep positioning stable while native controls are in use. */ }
      }));
    });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && currentDialog) closePreview(); });
    window.addEventListener('popstate', event => {
      closePreview(false);
      chooseNeed(hashNeed() || (validNeeds.has(event.state?.mfNeed) ? event.state.mfNeed : defaultNeed));
    });
    window.addEventListener('hashchange', () => {
      const need = hashNeed();
      // Ordinary anchors (hero CTA, guides) must not reset the selected care area.
      if (need) { closePreview(false); chooseNeed(need); scrollToSelection(); }
    });
    window.addEventListener('pagehide', () => { closePreview(false); paused(root); });
    const storedNeed = readState().need;
    chooseNeed(hashNeed() || (validNeeds.has(storedNeed) ? storedNeed : defaultNeed));
    history.replaceState({ ...history.state, mfNeed: activeNeed }, '', location.href);
    root.classList.add('mf-enhanced');
    if (hashNeed()) requestAnimationFrame(() => {
      const selection = root.querySelector('[data-mf-selection]');
      if (selection) selection.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
  };
  const boot = () => document.querySelectorAll('[data-mf-home]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
